import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO, generateImageGallerySchema, generateWebPageSchema } from '../../utils/seo';

const galleryItems = [
  {
    id: 1,
    title: 'Pack Premium',
    description: 'Nettoyage intérieur complet',
    category: 'interieur',
    beforeImage: 'https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/eff6bedc31007eb473d4964a67ee4829.jpeg',
    afterImage: 'https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/57756a9ee8d47789457bd3e7801847cc.jpeg',
  },
  {
    id: 2,
    title: 'Pack Luxe',
    description: 'Nettoyage intérieur + extérieur complet',
    category: 'exterieur',
    beforeImage: 'https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/9e4c79902af89144a76a5de550759868.jpeg',
    afterImage: 'https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/acfc91620f3fd7c53e7276106bedcde6.jpeg',
  },
];

export default function Galerie() {
  const [selectedImage, setSelectedImage] = useState<{url: string; title: string; type: string} | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredItems = activeFilter === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeFilter);

  // SEO Configuration
  const galleryImages = galleryItems.flatMap(item => [
    { url: item.beforeImage, title: `${item.title} - Avant`, description: item.description },
    { url: item.afterImage, title: `${item.title} - Après`, description: item.description }
  ]);

  useSEO({
    title: 'PRT Proprete - Galerie',
    description: 'Découvrez la qualité de nos prestations premium à travers nos images. Du nettoyage intérieur et extérieur à la finition haut de gamme, voyez par vous-même le soin et le professionnalisme que nous apportons à chaque véhicule. Déplacement gratuit dans un rayon de 35 km autour de Creil.',
    keywords: 'lavage auto Creil, préparateur auto, nettoyage voiture, detailing auto, lavage intérieur, lavage extérieur, Creil 60',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateImageGallerySchema(galleryImages),
        generateWebPageSchema('Galerie Avant/Après - PRT Propreté', 'Découvrez nos réalisations en images')
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-16 bg-gradient-to-br from-[#1a1a3e] via-[#2d2d6e] to-[#00B4D8] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 sm:w-64 h-32 sm:h-64 bg-[#E63946] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-48 sm:w-96 h-48 sm:h-96 bg-[#00B4D8] rounded-full blur-3xl animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center">
            <span className="text-[#00B4D8] font-bold text-sm sm:text-lg mb-2 sm:mb-4 block">NOS RÉALISATIONS</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 sm:mb-6">
              Galerie <span className="text-[#E63946]">Avant / Après</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
              Découvrez en détail la qualité de nos transformations
            </p>
          </div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-6 sm:py-8 px-4 bg-gray-50 sticky top-16 sm:top-20 md:top-24 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {[
              { key: 'all', label: 'Tout voir', icon: 'ri-apps-line' },
              { key: 'interieur', label: 'Pack Premium', icon: 'ri-car-line' },
              { key: 'exterieur', label: 'Pack Luxe', icon: 'ri-vip-crown-line' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`whitespace-nowrap px-4 sm:px-6 py-2 sm:py-3 rounded-full font-bold text-sm sm:text-base transition-all duration-300 cursor-pointer flex items-center gap-2 ${
                  activeFilter === filter.key
                    ? 'bg-gradient-to-r from-[#E63946] to-[#FF6B6B] text-white shadow-lg shadow-[#E63946]/30'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <i className={filter.icon}></i>
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
            {filteredItems.map((item) => (
              <div 
                key={item.id}
                className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500"
              >
                {/* Title */}
                <div className="p-6 sm:p-8 bg-gradient-to-r from-gray-900 to-gray-800">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white flex items-center gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#00B4D8] to-[#0096C7] rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`text-white text-2xl sm:text-3xl ${
                        item.category === 'interieur' ? 'ri-car-line' : 'ri-vip-crown-line'
                      }`}></i>
                    </div>
                    {item.title}
                  </h3>
                  <p className="text-gray-400 mt-3 text-base sm:text-lg">{item.description}</p>
                </div>

                {/* Before/After Images */}
                <div className="grid grid-cols-2 gap-2 p-2 bg-gray-100">
                  {/* Before */}
                  <div 
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedImage({ url: item.beforeImage, title: item.title, type: 'Avant' })}
                  >
                    <div className="w-full h-64 sm:h-80 md:h-96 overflow-hidden rounded-lg">
                      <img 
                        src={item.beforeImage}
                        alt={`${item.title} - Avant`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    {item.category === 'interieur' && (
                      <div className="absolute top-3 sm:top-4 left-3 sm:left-4 bg-[#E63946] text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-bold shadow-lg">
                        AVANT
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center rounded-lg">
                      <i className="ri-zoom-in-line text-white text-4xl sm:text-5xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                    </div>
                  </div>

                  {/* After */}
                  <div 
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedImage({ url: item.afterImage, title: item.title, type: 'Après' })}
                  >
                    <div className="w-full h-64 sm:h-80 md:h-96 overflow-hidden rounded-lg">
                      <img 
                        src={item.afterImage}
                        alt={`${item.title} - Après`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    {item.category === 'interieur' && (
                      <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-[#00B4D8] text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-bold shadow-lg">
                        APRÈS
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center rounded-lg">
                      <i className="ri-zoom-in-line text-white text-4xl sm:text-5xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4 sm:mb-6">
            Envie du même résultat ?
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8">
            Réservez votre prestation et transformez votre véhicule
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/prestations"
              className="whitespace-nowrap px-8 py-4 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white rounded-full font-bold hover:shadow-lg hover:shadow-[#00B4D8]/50 transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <i className="ri-star-line mr-2"></i>
              Voir nos prestations
            </Link>
            <Link 
              to="/contact"
              className="whitespace-nowrap px-8 py-4 bg-gradient-to-r from-[#E63946] to-[#FF6B6B] text-white rounded-full font-bold hover:shadow-lg hover:shadow-[#E63946]/50 transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              Réserver maintenant
            </Link>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-[#E63946] transition-colors duration-300 cursor-pointer flex items-center gap-2"
            >
              <span className="font-medium">Fermer</span>
              <i className="ri-close-line text-3xl"></i>
            </button>

            {/* Image */}
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="relative">
                <img 
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
                <div className={`absolute top-4 left-4 px-6 py-2 rounded-full text-white font-bold text-lg shadow-lg ${
                  selectedImage.type === 'Avant' ? 'bg-[#E63946]' : 'bg-[#00B4D8]'
                }`}>
                  {selectedImage.type}
                </div>
              </div>
              <div className="p-4 sm:p-6 bg-gradient-to-r from-gray-900 to-gray-800">
                <h3 className="text-xl sm:text-2xl font-black text-white">{selectedImage.title}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
